import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Search,
  Monitor,
  Image,
  ListVideo,
  Calendar,
  Folder,
  Rss,
  Smartphone,
  X,
} from 'lucide-react';
import { GLOBAL_SEARCH } from '@scio3/graphql-client';
import { isEnterKey } from '@/lib/keyboard';
import { usePermissions } from '@/hooks/usePermissions';
import { useTeamStore } from '@/stores/team-store';
import { useScreenFilterStore } from '@/features/screens/stores/screen-filter-store';
import { useAssetFilterStore } from '@/features/assets/stores/asset-filter-store';
import { usePlaylistFilterStore } from '@/features/playlists/stores/playlist-filter-store';
import { useScheduleSidebarStore } from '@/features/schedules/stores/schedule-sidebar-store';
import { useEngageFilterStore } from '@/features/engage/stores/engage-filter-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  Button,
} from '@scio3/ui';

interface GlobalSearchResult {
  globalSearch: {
    screens: Array<{
      _id: string;
      deviceName?: string;
      name?: string;
      status?: string;
      os?: string;
    }>;
    assets: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      thumbnail?: string;
    }>;
    playlists: Array<{ _id: string; name: string; tags?: string[] }>;
    schedules: Array<{ _id: string; name: string; tags?: string[] }>;
    folders: Array<{ _id: string; name: string; type?: string; path?: string }>;
    feeds: Array<{
      _id: string;
      name: string;
      tags?: string[];
      thumbnail?: string;
      categoryIds?: string[];
      author?: string;
    }>;
    engages: Array<{
      _id: string;
      name?: string;
      originalFileName?: string;
      type?: string;
      fileType?: string;
      appType?: string;
      thumbnail?: string;
    }>;
  };
}

const ENTITY_ROUTES: Record<string, string> = {
  screens: '/app/screenManagement',
  assets: '/app/assetManagement',
  playlists: '/app/playlistManagement',
  schedules: '/app/scheduleManagement',
  engages: '/app/engageManagement',
};

// Maps search entity types to usePermissions entity names for canEdit() checks
const ENTITY_PERMISSION_MAP: Record<string, 'screen' | 'asset' | 'playlist' | 'schedule'> = {
  screens: 'screen',
  assets: 'asset',
  playlists: 'playlist',
  schedules: 'schedule',
  engages: 'asset', // Engage uses asset permissions
};

function getCurrentEntityType(pathname: string): string | null {
  if (pathname.startsWith('/app/screenManagement')) return 'screens';
  if (pathname.startsWith('/app/assetManagement')) return 'assets';
  if (pathname.startsWith('/app/playlistManagement')) return 'playlists';
  if (pathname.startsWith('/app/scheduleManagement')) return 'schedules';
  if (pathname.startsWith('/app/engageManagement')) return 'engages';
  return null;
}

const PAGE_SEARCH_STORES: Record<
  string,
  { getState: () => { setSearchText: (t: string) => void } }
> = {
  screens: useScreenFilterStore,
  assets: useAssetFilterStore,
  playlists: usePlaylistFilterStore,
  schedules: useScheduleSidebarStore,
  engages: useEngageFilterStore,
};

const GROUP_ORDER = [
  'screens',
  'assets',
  'playlists',
  'schedules',
  'folders',
  'feeds',
  'engages',
] as const;

const FOLDER_TYPE_LABELS: Record<string, string> = {
  DEVICE: 'Screen',
  ASSET: 'Asset',
  PLAYLIST: 'Playlist',
  SCHEDULE: 'Schedule',
  ENGAGE: 'Engage',
};

/** Non-matching sentinel so cmdk doesn't auto-highlight the first item */
const NO_SELECTION = '__none__';

interface GlobalSearchBarProps {
  compact?: boolean;
}

export function GlobalSearchBar({ compact }: GlobalSearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTeamId } = useTeamStore();
  const { isRootSuperAdmin, isTeamAdmin, canEdit } = usePermissions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(NO_SELECTION);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Track whether user explicitly arrow-navigated (vs cmdk auto-selecting first item)
  const hasArrowNavigated = useRef(false);
  // Block cmdk auto-highlight until user interacts (arrow keys or mouse hover)
  const hasInteracted = useRef(false);

  const [executeSearch, { data, loading }] = useLazyQuery<GlobalSearchResult>(GLOBAL_SEARCH, {
    fetchPolicy: 'network-only',
  });

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when panel opens, clear query when it closes
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
    }
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    // Delay listener so the trigger click doesn't immediately close
    const id = setTimeout(() => document.addEventListener('mousedown', handleClick), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Debounced search
  const handleValueChange = useCallback(
    (value: string) => {
      setQuery(value);
      setSelectedValue(NO_SELECTION);
      hasArrowNavigated.current = false;
      hasInteracted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // Atlas Search autocomplete requires min 2 chars
      if (value.trim().length < 2) return;
      debounceRef.current = setTimeout(() => {
        executeSearch({
          variables: { teamId: activeTeamId, query: value.trim(), limit: 5 },
        });
      }, 300);
    },
    [activeTeamId, executeSearch],
  );

  // Navigate to entity page with deep-link.
  // Pre-set Zustand stores for playlists/schedules so their URL sync effects
  // don't race against the deep-link and overwrite it.
  const handleSelect = useCallback(
    (entityType: string, id: string, item: Record<string, unknown>) => {
      setOpen(false);
      const route = ENTITY_ROUTES[entityType];
      if (!route) return;

      // Build folder navigation URL from parentGroupInfo (shared by both permission checks)
      const parentGroup = item.parentGroupInfo as
        | {
            name?: string;
            path?: string | null;
            teamId?: string;
            isFolderSecurityEdit?: boolean;
          }
        | undefined;
      // Check permissions (Level 1: role + Level 2: folder security)
      const permEntity = ENTITY_PERMISSION_MAP[entityType];
      const hasRolePermission = !permEntity || canEdit(permEntity);
      let hasFolderPermission = true;
      if (parentGroup?.isFolderSecurityEdit === false) {
        const isSameTeam = !parentGroup.teamId || parentGroup.teamId === activeTeamId;
        hasFolderPermission = isRootSuperAdmin || (isTeamAdmin && isSameTeam);
      }

      if (entityType === 'playlists') {
        usePlaylistSelectionStore.getState().setActivePlaylist(id);
      } else if (entityType === 'schedules') {
        useScheduleSelectionStore.getState().selectSchedule(id);
      }

      // Build URL: always include folder path, add id only when user has permission
      const params = new URLSearchParams();
      if (parentGroup?.name) {
        const parentPath = parentGroup.path && parentGroup.path !== '/' ? parentGroup.path : '';
        params.set('path', `${parentPath}/${parentGroup.name}`);
        if (parentGroup.teamId && parentGroup.teamId !== activeTeamId) {
          params.set('teamId', parentGroup.teamId);
        }
      }

      if (!hasRolePermission || !hasFolderPermission) {
        // No permission — navigate to folder only + toast
        toast.error(t('You do not have permission to edit this item.'));
      } else {
        // Has permission — include id to trigger deep-link auto-open edit dialog
        params.set('id', id);
      }

      const qs = params.toString();
      navigate(qs ? `${route}?${qs}` : route);
    },
    [navigate, t, activeTeamId, isRootSuperAdmin, isTeamAdmin, canEdit],
  );

  const handleFeedSelect = useCallback(
    (id: string) => {
      setOpen(false);
      navigate(`/app/assetManagement?feed_id=${id}`);
    },
    [navigate],
  );

  const handleFolderSelect = useCallback(
    (folder: { _id: string; name: string; type?: string; path?: string; teamId?: string }) => {
      setOpen(false);
      const folderType = folder.type?.toUpperCase();
      // folder.type from API matches GROUP_TYPES values: device, asset, playlist, schedule, engage
      const routeMap: Record<string, string> = {
        DEVICE: '/app/screenManagement',
        ASSET: '/app/assetManagement',
        PLAYLIST: '/app/playlistManagement',
        SCHEDULE: '/app/scheduleManagement',
        ENGAGE: '/app/engage',
      };
      const route = routeMap[folderType ?? ''] ?? '/app/assetManagement';
      // Root-level folders have path=null or path='/'; nested have path='/Parent'
      const parentPath = folder.path && folder.path !== '/' ? folder.path : '';
      const folderPath = `${parentPath}/${folder.name}`;
      const params = new URLSearchParams({ path: folderPath });
      // Only include teamId for cross-team shared folders
      if (folder.teamId && folder.teamId !== activeTeamId) {
        params.set('teamId', folder.teamId);
      }
      navigate(`${route}?${params.toString()}`);
    },
    [navigate, activeTeamId],
  );

  // Order groups with current page entity first
  const currentEntity = getCurrentEntityType(location.pathname);
  const orderedGroups = currentEntity
    ? [currentEntity, ...GROUP_ORDER.filter((g) => g !== currentEntity)]
    : [...GROUP_ORDER];

  const results = data?.globalSearch;
  const hasResults =
    results &&
    (results.screens.length > 0 ||
      results.assets.length > 0 ||
      results.playlists.length > 0 ||
      results.schedules.length > 0 ||
      results.folders.length > 0 ||
      results.feeds.length > 0 ||
      results.engages.length > 0);

  const groupLabels: Record<string, string> = {
    screens: t('Screens'),
    assets: t('Files/Assets'),
    playlists: t('Playlists'),
    schedules: t('Schedules'),
    folders: t('Folders'),
    feeds: t('Feeds'),
    engages: t('Engage'),
  };

  const groupIcons: Record<string, React.ReactNode> = {
    screens: <Monitor className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    assets: <Image className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    playlists: <ListVideo className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    schedules: <Calendar className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    folders: <Folder className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    feeds: <Rss className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
    engages: <Smartphone className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />,
  };

  function getItemName(entityType: string, item: Record<string, unknown>): string {
    if (entityType === 'screens') {
      return (item.deviceName as string) || (item.name as string) || (item._id as string);
    }
    if (entityType === 'assets' || entityType === 'engages') {
      return (
        (item.originalFileName as string) ||
        (item.appType as string) ||
        (item.name as string) ||
        'Untitled'
      );
    }
    return (item.name as string) || (item._id as string);
  }

  function getFolderSubtitle(item: Record<string, unknown>): string | null {
    const type = item.type as string | undefined;
    if (!type) return null;
    const label = FOLDER_TYPE_LABELS[type.toUpperCase()];
    return label ? `${t(label)} ${t('Folder')}` : null;
  }

  function renderGroup(entityType: string) {
    if (!results) return null;
    const items = results[entityType as keyof typeof results] as Array<Record<string, unknown>>;
    if (!items || items.length === 0) return null;

    return (
      <CommandGroup key={entityType} heading={groupLabels[entityType]}>
        {items.map((item) => (
          <CommandItem
            key={item._id as string}
            value={`${entityType}-${item._id}`}
            onSelect={() => {
              if (entityType === 'folders') {
                handleFolderSelect(
                  item as {
                    _id: string;
                    name: string;
                    type?: string;
                    path?: string;
                    teamId?: string;
                  },
                );
              } else if (entityType === 'feeds') {
                handleFeedSelect(item._id as string);
              } else {
                handleSelect(entityType, item._id as string, item);
              }
            }}
          >
            {groupIcons[entityType]}
            <div className="flex min-w-0 flex-col">
              <span className="truncate">{getItemName(entityType, item)}</span>
              {entityType === 'folders' && getFolderSubtitle(item) && (
                <span className="truncate text-xs text-muted-foreground">
                  {getFolderSubtitle(item)}
                </span>
              )}
              {entityType === 'feeds' && (item.author as string) && (
                <span className="truncate text-xs text-muted-foreground">
                  {item.author as string}
                </span>
              )}
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  return (
    <>
      {/* Trigger in nav bar */}
      {compact ? (
        <Button
          variant="ghost"
          size="icon-sm"
          className="ml-2 text-muted-foreground hover:text-foreground hover:bg-accent"
          aria-label={t('Search')}
          onClick={() => setOpen(true)}
        >
          <Search className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="ml-2 h-8 w-56 justify-start gap-2 text-muted-foreground"
          onClick={() => setOpen(true)}
        >
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs">{t('Search...')}</span>
          <kbd className="pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">&#8984;</span>K
          </kbd>
        </Button>
      )}

      {/* Floating search panel — centered in the nav, no backdrop */}
      {open && (
        <div
          ref={panelRef}
          className="fixed left-1/2 top-3 z-[60] w-[480px] -translate-x-1/2 rounded-xl border border-border bg-card shadow-[0_8px_30px_rgba(0,0,0,0.16)]"
        >
          <Command
            shouldFilter={false}
            value={selectedValue}
            onValueChange={(v) => {
              if (hasInteracted.current) setSelectedValue(v);
            }}
          >
            <div className="flex items-center border-b px-3 py-0.5">
              <Search className="mr-2.5 h-4.5 w-4.5 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                className="flex h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                placeholder={t('Search screens, assets, playlists, schedules...')}
                value={query}
                onChange={(e) => handleValueChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    hasInteracted.current = true;
                    hasArrowNavigated.current = true;
                  }
                  if (isEnterKey(e) && !hasArrowNavigated.current) {
                    // User typed and hit Enter without arrow-navigating — search on current page
                    e.preventDefault();
                    const entity = getCurrentEntityType(location.pathname);
                    const trimmed = query.trim();
                    if (!entity || !trimmed) return;
                    PAGE_SEARCH_STORES[entity].getState().setSearchText(trimmed);
                    setOpen(false);
                  }
                  // If user arrow-navigated, let cmdk handle Enter (fires onSelect)
                }}
              />
              {query && (
                <button
                  className="ml-2 rounded-sm opacity-50 hover:opacity-100"
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                  aria-label={t('Clear search')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <CommandList
              className="max-h-[360px]"
              onMouseMove={() => {
                hasInteracted.current = true;
              }}
            >
              {loading && query.trim() && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('Searching...')}
                </div>
              )}
              {!loading && query.trim().length === 1 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('Type one more character to search...')}
                </div>
              )}
              {!loading && query.trim().length >= 2 && !hasResults && (
                <CommandEmpty>{t('No results found.')}</CommandEmpty>
              )}
              {!loading && query.trim() && hasResults && (
                <>
                  {orderedGroups.map((entityType, i) => {
                    const group = renderGroup(entityType);
                    if (!group) return null;
                    return (
                      <div key={entityType}>
                        {i > 0 && <CommandSeparator />}
                        {group}
                      </div>
                    );
                  })}
                </>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </>
  );
}
